/**
 * Device Detection Utility
 * Handles device type detection and view preference management for mobile experience
 */

/**
 * Checks if the current device is a mobile device (phone or tablet)
 * Uses user agent detection combined with viewport width check
 */
export const isMobileDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
  return mobileRegex.test(userAgent.toLowerCase()) || window.innerWidth <= 768;
};

/**
 * Returns the specific device type
 * @returns {'phone' | 'tablet' | 'desktop'}
 */
export const getDeviceType = () => {
  if (isMobileDevice()) {
    return window.innerWidth <= 480 ? 'phone' : 'tablet';
  }
  return 'desktop';
};

/**
 * Stores the user's view preference in localStorage
 * @param {'mobile' | 'default'} preference - The view preference to store
 */
export const setViewPreference = (preference) => {
  localStorage.setItem('viewPreference', preference);
};

/**
 * Retrieves the user's view preference from localStorage
 * @returns {'mobile' | 'default' | 'auto'} - The stored preference or 'auto' if not set
 */
export const getViewPreference = () => {
  return localStorage.getItem('viewPreference') || 'auto';
};

/**
 * Clears the view preference from localStorage
 */
export const clearViewPreference = () => {
  localStorage.removeItem('viewPreference');
};

/**
 * Determines if the user should see the mobile view based on device and preference
 * @returns {boolean} - True if mobile view should be shown
 */
export const shouldShowMobileView = () => {
  const preference = getViewPreference();
  
  if (preference === 'default') {
    return false;
  }
  
  if (preference === 'mobile') {
    return true;
  }
  
  // Auto mode - show mobile view on mobile devices
  return isMobileDevice();
};

/**
 * Checks if touch events are supported
 * @returns {boolean}
 */
export const isTouchDevice = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};
